import type { DehydratedState } from "@tanstack/react-query";
import { createRoot, hydrateRoot } from "react-dom/client";
import App, { preloadRouteForPath } from "./App";
import "./index.css";
import { initGoogleAnalytics } from "./lib/googleAnalytics";
import { initMetaPixel } from "./lib/metaPixel";

// Initialize the Meta Pixel ASAP so the _fbp cookie is set early.
initMetaPixel();
// Initialize Google Analytics (gtag.js) early so the first page_view is captured.
initGoogleAnalytics();

type PrerenderPayload = {
    dehydratedState?: unknown;
};

const rootElement = document.getElementById("root");

if (!rootElement) {
    throw new Error("Root element #root was not found");
}

const prerenderPayload = (() => {
    const payloadNode = document.getElementById("prerender-data");
    if (!payloadNode?.textContent) return null;

    try {
        return JSON.parse(payloadNode.textContent) as PrerenderPayload;
    } catch {
        return null;
    }
})();

const app = (
    <App
        dehydratedState={
            prerenderPayload?.dehydratedState as DehydratedState | undefined
        }
    />
);

const mount = () => {
    if (rootElement.hasChildNodes()) {
        hydrateRoot(rootElement, app);
    } else {
        createRoot(rootElement).render(app);
    }
};

// Preload the current route's code-split chunk BEFORE hydrating so the first
// client render matches the prerendered HTML (no hydration mismatch). Home is
// eager, so this resolves immediately there. Never blocks mount on failure.
preloadRouteForPath(window.location.pathname).then(mount, mount);
